'use client'

import React, { useRef, useCallback } from 'react'
import { Upload, FolderOpen } from 'lucide-react'
import { cn } from '../../../utils/styles'
import type { FileDropZoneProps } from '../types'

export function FileDropZone({
  accept,
  multiple = false,
  disabled = false,
  isDragging,
  onDragOver,
  onDragLeave,
  onDrop,
  onClick,
  children,
  className,
  ariaLabel = 'File upload area'
}: FileDropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const handleClick = useCallback(() => {
    if (disabled) return
    inputRef.current?.click()
  }, [disabled])

  const handleFileSelect = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return

    // Create a synthetic drop event for consistency
    const syntheticEvent = {
      preventDefault: () => {},
      dataTransfer: { files }
    } as React.DragEvent

    onDrop(syntheticEvent)
  }, [onDrop])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (disabled) return

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClick()
    }
  }, [disabled, handleClick])

  return (
    <div
      className={cn(
        'relative border-2 border-dashed rounded-lg transition-all duration-200 ease-in-out',
        'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
        {
          // Normal state
          'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100': !isDragging && !disabled,

          // Dragging state
          'border-blue-500 bg-blue-50 ring-2 ring-blue-500 ring-offset-2': isDragging && !disabled,

          // Disabled state
          'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed': disabled,

          // Interactive cursor
          'cursor-pointer': !disabled
        },
        className
      )}
      onDragOver={!disabled ? onDragOver : undefined}
      onDragEnter={!disabled ? onDragOver : undefined}
      onDragLeave={!disabled ? onDragLeave : undefined}
      onDrop={!disabled ? onDrop : undefined}
      onClick={!disabled ? handleClick : undefined}
      onKeyDown={!disabled ? handleKeyDown : undefined}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-label={ariaLabel}
      aria-describedby={accept ? 'file-upload-accept-types' : undefined}
      aria-disabled={disabled}
    >
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept?.join(',')}
        multiple={multiple}
        disabled={disabled}
        onChange={(e) => handleFileSelect(e.target.files)}
        className="sr-only"
        aria-hidden="true"
      />

      {children}
    </div>
  )
}

// Default content for empty drop zone
export interface FileDropZoneContentProps {
  isDragging: boolean
  accept?: string[]
  multiple?: boolean
  maxSize?: number
  dragDropText?: string
  browseText?: string
  emptyStateText?: string
  disabled?: boolean
  className?: string
}

export function FileDropZoneContent({
  isDragging,
  accept,
  multiple = false,
  maxSize,
  dragDropText,
  browseText = 'browse files',
  emptyStateText,
  disabled = false,
  className
}: FileDropZoneContentProps) {
  const formatAcceptedTypes = () => {
    if (!accept || accept.length === 0) return 'any file'

    const types = accept.map(type => {
      if (type.includes('*')) {
        return type.replace('/*', ' files')
      }
      return type.replace('application/', '').replace('image/', '').toUpperCase()
    })

    return types.join(', ')
  }

  const formatMaxSize = () => {
    if (!maxSize) return ''

    const mb = maxSize / (1024 * 1024)
    if (mb >= 1) {
      return `(max ${mb.toFixed(0)}MB)`
    }

    const kb = maxSize / 1024
    return `(max ${kb.toFixed(0)}KB)`
  }

  const getMainText = () => {
    if (isDragging) {
      return multiple ? 'Drop files here' : 'Drop file here'
    }

    if (dragDropText) return dragDropText

    if (emptyStateText) return emptyStateText

    return multiple
      ? 'Drag and drop files here, or click to select files'
      : 'Drag and drop a file here, or click to select'
  }

  return (
    <div className={cn('p-8 text-center', className)}>
      <div className="flex flex-col items-center space-y-4">
        {/* Icon */}
        <div className={cn(
          'flex items-center justify-center w-16 h-16 rounded-full transition-colors',
          {
            'bg-blue-100 text-blue-600': isDragging,
            'bg-gray-200 text-gray-400': !isDragging && !disabled,
            'bg-gray-100 text-gray-300': disabled
          }
        )}>
          {isDragging ? (
            <FolderOpen className="w-8 h-8" />
          ) : (
            <Upload className="w-8 h-8" />
          )}
        </div>

        {/* Main text */}
        <div className="space-y-2">
          <p className={cn(
            'text-lg font-medium',
            {
              'text-blue-700': isDragging,
              'text-gray-700': !isDragging && !disabled,
              'text-gray-400': disabled
            }
          )}>
            {getMainText()}
          </p>

          <div className="space-y-1">
            <p className="text-sm text-gray-500">
              Accepted types: {formatAcceptedTypes()} {formatMaxSize()}
            </p>

            {!isDragging && !disabled && (
              <p className="text-sm text-gray-400">
                or <span className="text-blue-600 hover:text-blue-700 underline">{browseText}</span>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Compact version for smaller spaces
export interface FileDropZoneCompactProps extends Omit<FileDropZoneProps, 'children'> {
  text?: string
  icon?: React.ReactNode
}

export function FileDropZoneCompact({
  text = 'Choose files',
  icon,
  className,
  ...props
}: FileDropZoneCompactProps) {
  return (
    <FileDropZone
      {...props}
      className={cn('px-4 py-3', className)}
    >
      <div className="flex items-center justify-center space-x-2">
        {icon || <Upload className="w-4 h-4" />}
        <span className="text-sm font-medium">{text}</span>
      </div>
    </FileDropZone>
  )
}

// Zone with preview area
export interface FileDropZoneWithPreviewProps extends FileDropZoneProps {
  files: File[]
  onRemove?: (index: number) => void
  showThumbnails?: boolean
  thumbnailSize?: number
  emptyContent?: React.ReactNode
}

export function FileDropZoneWithPreview({
  files,
  onRemove,
  showThumbnails = true,
  thumbnailSize = 48,
  emptyContent,
  children,
  className,
  ...props
}: FileDropZoneWithPreviewProps) {
  const hasFiles = files.length > 0

  return (
    <FileDropZone
      {...props}
      className={cn(
        {
          'p-4': hasFiles,
          'min-h-32': !hasFiles
        },
        className
      )}
    >
      {hasFiles ? (
        <div className="space-y-4">
          {/* File list */}
          <div className="flex flex-wrap gap-2">
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                className="flex items-center space-x-2 bg-white rounded border border-gray-200 p-2"
              >
                {showThumbnails && (
                  <div
                    className="flex-shrink-0 bg-gray-100 rounded border"
                    style={{ width: thumbnailSize, height: thumbnailSize }}
                  >
                    {/* Thumbnail would be rendered here */}
                    <div className="w-full h-full flex items-center justify-center text-xs">
                      📎
                    </div>
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>

                {onRemove && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onRemove(index)
                    }}
                    className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    aria-label={`Remove ${file.name}`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add more files area */}
          <div className="border-t pt-4">
            <div className="text-center">
              <p className="text-sm text-gray-600">Drop more files here or click to add</p>
            </div>
          </div>
        </div>
      ) : (
        emptyContent || children
      )}
    </FileDropZone>
  )
}